import { pget, ppost } from "@/api";
import { Models } from "@/api/types";
import { formatPlatformNameForReferral } from "@/organisms/refer-a-friend/utils";

import {
  getSignupRewardDiscountType,
  getSignupRewardInclusions,
  getSignupRewardTitle,
} from "../rewards/rewards.utils";

export const getAvailableReferralRewardForPlatformLocation = pget(
  "/incentives/{platformLocationId}/available-referral-reward"
);

export const generatePersonalCode = ppost(
  "/referral-codes/personal-code/{personId}"
);

export const generateStatelessShareCode = ppost(
  "/referral-codes/stateless-share-url/{personalReferralCode}/platform/{platformShareInvite}"
);

export const getAwardedReferralRewards = pget(
  "/incentives/{platformId}/referral-rewards/{personId}/redeemable"
);
type GenerateReferralShareTextParams = {
  platformName: string;
  rewardTitle: string;
};

export const generateReferralShareText = ({
  platformName,
  rewardTitle,
}: GenerateReferralShareTextParams): string => {
  const formattedPlatformName = formatPlatformNameForReferral(platformName);

  return `Hey! I just had an amazing time at ${formattedPlatformName} and thought you'd love it. Here's ${rewardTitle} to try it out yourself! Book now: `;
};

export function getReferralRewardTitle(
  referralReward?: Models<"DisplayAvailableReferralReward"> | null,
  activeSignupReward?: Models<"DisplayActiveReward"> | null
) {
  if (!referralReward || !activeSignupReward) {
    return "Referral Rewards";
  }

  const itemName = referralReward.rewardTitle.text;

  if (referralReward.referralRewardType === "RepeatCash") {
    return `The easiest ${itemName} you'll ever earn.`;
  }

  if (referralReward.referralRewardType === "ComplimentaryItem") {
    return `The easiest ${itemName} you'll ever earn.`;
  }

  if (referralReward.referralRewardType === "InOfficeReward") {
    return "The easiest way to earn something worth showing off.";
  }
}

export function getReferralRewardDescription(
  referralReward?: Models<"DisplayAvailableReferralReward"> | null,
  signupRewardText = "rewarded, too"
) {
  if (!referralReward || !signupRewardText) {
    return "See the history of your referrals";
  }

  const itemName = referralReward.rewardTitle.text;

  if (referralReward.referralRewardType === "RepeatCash") {
    return `You'll both win when you refer. You'll get ${itemName} and they'll get ${signupRewardText}.`;
  }

  if (referralReward.referralRewardType === "ComplimentaryItem") {
    return `You'll both win when you refer. You'll get a ${itemName} and they'll get ${signupRewardText}.`;
  }

  if (referralReward.referralRewardType === "InOfficeReward") {
    return `You both win when you refer. You'll get ${itemName} and they'll get ${signupRewardText}.`;
  }
}

export const getReferralRewardCardTitle = (
  referralReward?: Models<"DisplayAvailableReferralReward"> | null
) => {
  if (!referralReward) {
    return "Referral history";
  }

  const repeatCashAmount =
    Number(referralReward.repeatCashAmountInCents ?? 0) / 100;

  if (referralReward.referralRewardType === "RepeatCash") {
    return `The easiest $${repeatCashAmount} you'll ever earn.`;
  }

  if (referralReward.referralRewardType === "ComplimentaryItem") {
    return "The easiest way to earn something worth showing off.";
  }

  if (referralReward.referralRewardType === "InOfficeReward") {
    return "Get rewarded for bringing a friend.";
  }
};

export const getReferralRewardCardDescription = (
  referralReward?: Models<"DisplayAvailableReferralReward"> | null,
  signupReward?: Models<"DisplayActiveReward"> | null
) => {
  if (!referralReward || !signupReward) {
    return "See your past referrals.";
  }

  const signupRewardDiscountType = getSignupRewardDiscountType(signupReward);

  const repeatCashAmount =
    Number(referralReward.repeatCashAmountInCents ?? 0) / 100;

  const amountOff = Number(signupReward.discount?.amountOff ?? 0) / 100;

  const discountPercentOff =
    Number(signupReward.discount?.percentOff ?? 0) * 100;

  // Repeat Cash
  if (
    referralReward.referralRewardType === "RepeatCash" &&
    signupRewardDiscountType === "AmountOff"
  ) {
    return `You'll both win when you refer. You'll get $${repeatCashAmount} and they'll get $${amountOff} off.`;
  }

  if (
    referralReward.referralRewardType === "RepeatCash" &&
    signupRewardDiscountType === "PercentOff"
  ) {
    return `You'll both win when you refer. You'll get $${repeatCashAmount} and they'll get ${discountPercentOff}% off.`;
  }

  if (
    referralReward.referralRewardType === "RepeatCash" &&
    signupRewardDiscountType === "FreeItemAll"
  ) {
    return `You'll both win when you refer. You'll get $${repeatCashAmount} and they'll get a free items.`;
  }

  if (
    referralReward.referralRewardType === "RepeatCash" &&
    signupRewardDiscountType === "FreeItemSingle"
  ) {
    return `You'll both win when you refer. You'll get $${repeatCashAmount} and they'll get a free items.`;
  }

  if (
    referralReward.referralRewardType === "RepeatCash" &&
    signupRewardDiscountType === "FreeItemChooseFrom"
  ) {
    return `You'll both win when you refer. You'll get $${repeatCashAmount} and they can select from free items.`;
  }

  // Complimentary Item
  if (
    referralReward.referralRewardType === "ComplimentaryItem" &&
    signupRewardDiscountType === "AmountOff"
  ) {
    return `You'll both win when you refer. You'll get a ${referralReward.rewardTitle.text} and they'll get $${amountOff} off.`;
  }

  if (
    referralReward.referralRewardType === "ComplimentaryItem" &&
    signupRewardDiscountType === "PercentOff"
  ) {
    return `You'll both win when you refer. You'll get a ${referralReward.rewardTitle.text} and they'll get ${discountPercentOff}% off.`;
  }

  if (
    referralReward.referralRewardType === "ComplimentaryItem" &&
    signupRewardDiscountType === "FreeItemAll"
  ) {
    return `You'll both win when you refer. You'll get a ${referralReward.rewardTitle.text} and they'll get a free items.`;
  }

  if (
    referralReward.referralRewardType === "ComplimentaryItem" &&
    signupRewardDiscountType === "FreeItemSingle"
  ) {
    return `You'll both win when you refer. You'll get a ${referralReward.rewardTitle.text} and they'll get a free items.`;
  }

  if (
    referralReward.referralRewardType === "ComplimentaryItem" &&
    signupRewardDiscountType === "FreeItemChooseFrom"
  ) {
    return `You'll both win when you refer. You'll get a ${referralReward.rewardTitle.text} and they can select from free items.`;
  }

  // in office
  if (
    referralReward.referralRewardType === "InOfficeReward" &&
    signupRewardDiscountType === "AmountOff"
  ) {
    return `You'll both win when you refer. You'll get a ${referralReward.rewardTitle.text} and they'll get $${amountOff} off.`;
  }

  if (
    referralReward.referralRewardType === "InOfficeReward" &&
    signupRewardDiscountType === "PercentOff"
  ) {
    return `You'll both win when you refer. You'll get a ${referralReward.rewardTitle.text} and they'll get ${discountPercentOff}% off.`;
  }

  if (
    referralReward.referralRewardType === "InOfficeReward" &&
    signupRewardDiscountType === "FreeItemAll"
  ) {
    return `You'll both win when you refer. You'll get a ${referralReward.rewardTitle.text} and they'll get a free items.`;
  }

  if (
    referralReward.referralRewardType === "InOfficeReward" &&
    signupRewardDiscountType === "FreeItemSingle"
  ) {
    return `You'll both win when you refer. You'll get a ${referralReward.rewardTitle.text} and they'll get a free items.`;
  }

  if (
    referralReward.referralRewardType === "InOfficeReward" &&
    signupRewardDiscountType === "FreeItemChooseFrom"
  ) {
    return `You'll both win when you refer. You'll get a ${referralReward.rewardTitle.text} and they can select from free items.`;
  }

  return `You'll both win when you refer. You'll get ${referralReward.rewardTitle.text} and they'll get ${signupReward.title}.`;
};

export const getReferralRewardCurrentPatientRewardTitle = (
  referralReward: Models<"DisplayAvailableReferralReward">
) => {
  if (referralReward.referralRewardType === "RepeatCash") {
    return `$${
      (referralReward.repeatCashAmountInCents || 0) / 100
    } for referring a friend.`;
  }

  if (referralReward.referralRewardType === "ComplimentaryItem") {
    return `Free ${referralReward.rewardTitle.text} for referring a friend.`;
  }

  return `${referralReward.rewardTitle.text}`;
};

export const getReferralRewardNewPatientRewardTitle = (
  signupReward: Models<"DisplayActiveReward">,
  referrerName: string
) => {
  const signupRewardDiscountType = getSignupRewardDiscountType(signupReward);

  const suffix = getSignupRewardTitle(signupReward);

  if (signupRewardDiscountType === "AmountOff") {
    return `${suffix} thanks to ${referrerName}.`;
  }

  if (signupRewardDiscountType === "PercentOff") {
    return `${suffix} thanks to ${referrerName}.`;
  }

  if (signupRewardDiscountType === "FreeItemAll") {
    return `${suffix} thanks to ${referrerName}.`;
  }

  if (signupRewardDiscountType === "FreeItemSingle") {
    return `${suffix} thanks to ${referrerName}.`;
  }

  if (signupRewardDiscountType === "FreeItemChooseFrom") {
    return `${suffix} thanks to ${referrerName}.`;
  }

  return `${suffix} thanks to ${referrerName}.`;
};
